import { scheduleBlockOutcome } from "../booking/scheduleBlockOutcome.js";
import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useMemo, useRef, useState } from "react";
import {
  ScheduleBlockForm,
  ScheduleBlockSafetyNotice,
  scheduleBlockFormFromDiary,
  toInput,
} from "../booking/ScheduleBlockForm.js";
import type { ScheduleBlockTarget } from "../booking/scheduling.js";
import { scheduleBlocks, type StaffScheduleBlock } from "../booking/scheduleBlocks.js";
import { formatDate, formatTime } from "../format.js";
import { Drawer } from "../ui/Dialog.js";
import type { DiaryEntry } from "./entries.js";
import { BookLessonDrawer } from "./BookLessonDrawer.js";
import type { BookingSuggestionRange } from "./bookingSuggestions.js";
import type { BlankDiarySlot } from "./TimeGrid.js";

function UndoScheduleBlockButton({ onUndo }: { onUndo: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const running = useRef(false);

  async function undo() {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    try {
      await onUndo();
    } finally {
      running.current = false;
      setBusy(false);
    }
  }

  return <button type="button" disabled={busy} onClick={() => void undo()}>{busy ? "Removing…" : "Undo"}</button>;
}

export function BlankSlotDrawer({
  slot,
  target,
  targetLoading = false,
  targetError = null,
  onClose,
  suggestionEntries = [],
  suggestionRange = null,
  onBooked = () => undefined,
  onBlocked,
}: {
  slot: BlankDiarySlot;
  /** Provider this slot's diary tutor resolves to, or null when unresolved. */
  target: ScheduleBlockTarget | null;
  /** The identity the page needs to resolve `target` is still loading. */
  targetLoading?: boolean;
  /** Loading that identity failed. */
  targetError?: string | null;
  onClose: () => void;
  onBookLesson?: () => void;
  suggestionEntries?: readonly DiaryEntry[];
  suggestionRange?: BookingSuggestionRange | null;
  onBooked?: () => void;
  onBlocked: (block: StaffScheduleBlock) => void;
}) {
  const publish = usePublishActionOutcome();
  const [result, setResult] = useState<ActionOutcome | null>(null);
  const [action, setAction] = useState<"book" | "block">("book");
  const [busy, setBusy] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [blockNotice, setBlockNotice] = useState<string | null>(null);
  const [blockProblem, setBlockProblem] = useState<string | null>(null);
  const actionInFlight = useRef(false);
  const studentSearchRef = useRef<HTMLInputElement>(null);
  const blockInitial = useMemo(
    () => scheduleBlockFormFromDiary(slot.day, slot.minute),
    [slot.day, slot.minute],
  );

  // A resolved target is enough to block: it only comes back non-null once the
  // page has real identity for this tutor, so a still-pending or failed load of
  // the *other* resolution path must not veto it.
  const canBlock = target !== null;

  async function blockSelectedHour() {
    if (target === null || busy || actionInFlight.current) return;
    actionInFlight.current = true;
    setBusy(true);
    setBlockNotice(null);
    setBlockProblem(null);
    const selectedTarget = target;
    let createdBlock: StaffScheduleBlock | null = null;

    try {
      const input = toInput(blockInitial);
      const preview = await scheduleBlocks.preview(selectedTarget.providerId, input);
      if (preview.conflict_count > 0) {
        setBlockNotice("Lessons are booked in this time. Review them below.");
        setAction("block");
        return;
      }

      const applied = await scheduleBlocks.create(selectedTarget.providerId, input);
      createdBlock = applied.block;
      const outcome: ActionOutcome = {
        ...scheduleBlockOutcome(applied.block, "Time blocked"),
        action: (
          <UndoScheduleBlockButton onUndo={async () => {
            try {
              const removed = await scheduleBlocks.remove(selectedTarget.providerId, applied.block.id);
              publish(scheduleBlockOutcome(removed, "Blocked time removed", true));
              onBlocked(applied.block);
            } catch (cause: unknown) {
              publish({
                variant: "attention",
                title: "Could not undo blocked time",
                details: [{ value: cause instanceof Error ? cause.message : "Refresh the diary and check the schedule before trying again." }],
              });
            }
          }} />
        ),
      };
      if (!publish(outcome)) setResult(outcome);
    } catch (cause: unknown) {
      setBlockProblem(
        cause instanceof Error
          ? cause.message
          : "We could not confirm that the time was blocked. Refresh the diary and check before trying again.",
      );
    } finally {
      actionInFlight.current = false;
      setBusy(false);
    }

    if (createdBlock) onBlocked(createdBlock);
  }

  return (
    <Drawer
      eyebrow="Diary"
      title={action === "book" ? "Book a lesson" : "Block time"}
      description={(
        <>
          {formatDate(slot.startsAt)} · {formatTime(slot.startsAt)}
          {target ? ` · ${target.displayName}` : ""}
        </>
      )}
      onClose={onClose}
      busy={busy || bookingBusy}
      closeLabel="Close"
      className="cal-lesson-drawer cal-blank-drawer cal-booking-drawer"
      size="wide"
      initialFocusRef={studentSearchRef}
    >
      {result ? <ActionConfirmation {...result} /> : (
        <>
          <div className="cal-blank-actions">
            {action === "book" ? (
              <button className="action-secondary" type="button" disabled={!canBlock || busy || bookingBusy} onClick={() => { setBlockNotice(null); setBlockProblem(null); setAction("block"); }}>
                Choose times to block
              </button>
            ) : (
              <button className="quiet small cal-drawer-back" type="button" disabled={busy} onClick={() => setAction("book")}>
                ← Back to booking
              </button>
            )}
            <button className="action-secondary" type="button" disabled={!canBlock || busy || bookingBusy} onClick={() => void blockSelectedHour()}>
              Block {blockInitial.startTime}–{blockInitial.endTime}
            </button>
          {blockProblem && <div className="error" role="alert">{blockProblem}</div>}
          {!canBlock && targetLoading && <p className="small muted" role="status">Checking block access…</p>}
          {!canBlock && !targetLoading && targetError && (
            <p className="small muted">We could not confirm which tutor you may block right now.</p>
          )}
          {!canBlock && !targetLoading && !targetError && (
            <p className="small muted">
              Block time is available only when this diary slot identifies a tutor you are authorised to manage.
            </p>
          )}
          </div>
          {action === "book" ? (
            <BookLessonDrawer
              inline
              hint={{ startsAt: slot.startsAt, ...(slot.tutorId ? { tutorId: slot.tutorId } : {}) }}
              suggestionEntries={suggestionEntries}
              suggestionRange={suggestionRange}
              studentSearchRef={studentSearchRef}
              onClose={onClose}
              onBooked={onBooked}
              onMutationBusyChange={setBookingBusy}
            />
          ) : target !== null ? (
            <>
              <p className="small cal-block-target">
                Tutor: <strong>{target.displayName}</strong>
              </p>
              {blockNotice && <p className="small muted" role="status">{blockNotice}</p>}
              {blockProblem && <div className="error" role="alert">{blockProblem}</div>}
              <ScheduleBlockSafetyNotice />
              <ScheduleBlockForm
                providerId={target.providerId}
                initial={blockInitial}
                onBusyChange={setBusy}
                onSaved={(applied, combinedOutcome) => {
                  const outcome = combinedOutcome ?? scheduleBlockOutcome(applied.block, "Time blocked");
                  if (!publish(outcome)) setResult(outcome);
                  onBlocked(applied.block);
                }}
              />
            </>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
